// /app/frontend/src/lib/qzTray.js
// Servicio de impresión directa - ComaLab Print Server
// Conecta con print_server.py corriendo en localhost:9120

// Configuración de impresoras
export const PRINTERS = {
  ETIQUETAS: ["Etiquetas", "Etiquetas1"],
  RECIBOS: "Canon G3070 series",
  // Nombre EXACTO preferido de la impresora térmica de 58mm (Xprinter XP-P502A
  // instalada como "POS-58" en Windows). Se prioriza este nombre exacto.
  TICKET58_NAME: "POS-58",
  // Patrones de respaldo si no existe el nombre exacto.
  TICKET58_PATTERNS: [/^pos.?58$/i, /pos.?58/i, /p502/i, /xprinter/i, /58\s?mm/i, /thermal/i]
};

const PRINT_SERVER_URL = "http://localhost:9120";
const TIMEOUT = 8000;

// Timeout helper
const fetchWithTimeout = (url, options = {}, ms = TIMEOUT) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

// Verificar si el servidor de impresión está corriendo
export const checkPrintServer = async () => {
  try {
    const res = await fetchWithTimeout(`${PRINT_SERVER_URL}/status`, {}, 3000);
    const data = await res.json();
    return { available: true, sumatra: data.sumatra };
  } catch {
    return { available: false, sumatra: false };
  }
};

// Obtener lista de impresoras
export const getPrinters = async () => {
  try {
    const res = await fetchWithTimeout(`${PRINT_SERVER_URL}/printers`);
    const data = await res.json();
    return data.printers || [];
  } catch {
    return [];
  }
};

// Convertir blob URL a base64
const urlToBase64 = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Imprimir PDF directamente
export const printPDF = async (pdfUrl, printerName, settings = "") => {
  try {
    // Verificar servidor
    const status = await checkPrintServer();
    if (!status.available) {
      throw new Error(
        "Servidor de impresión no disponible.\n" +
        "Ejecuta 'iniciar_print_server.bat' en tu computadora."
      );
    }
    if (!status.sumatra) {
      throw new Error(
        "SumatraPDF no encontrado.\n" +
        "Descárgalo y coloca SumatraPDF.exe en la carpeta del print server."
      );
    }

    // Convertir PDF a base64
    const pdfBase64 = await urlToBase64(pdfUrl);

    // Enviar a imprimir
    const res = await fetchWithTimeout(`${PRINT_SERVER_URL}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pdf: pdfBase64, printer: printerName, settings })
    });

    let result;
    try {
      result = await res.json();
    } catch {
      result = { success: false, message: `Error del servidor (código: ${res.status})` };
    }
    return result;
  } catch (error) {
    if (error.name === 'AbortError') {
      return { success: false, message: "Timeout: el servidor no respondió a tiempo" };
    }
    return { success: false, message: error.message };
  }
};

// Imprimir etiquetas (horizontal/landscape) - busca automáticamente la impresora disponible
export const printEtiquetas = async (pdfUrl) => {
  // Buscar cuál impresora de etiquetas está disponible en esta PC (match exacto)
  const printers = await getPrinters();
  const labelPrinter = printers.find(p => 
    PRINTERS.ETIQUETAS.some(name => p.toLowerCase() === name.toLowerCase())
  );
  
  if (!labelPrinter) {
    return { success: false, message: `Impresora de etiquetas no encontrada.\nBuscando: ${PRINTERS.ETIQUETAS.join(", ")}\nDisponibles: ${printers.join(", ")}` };
  }
  
  return printPDF(pdfUrl, labelPrinter, "landscape,fit");
};

// Imprimir recibo (vertical/portrait, blanco y negro)
export const printRecibo = async (pdfUrl) => {
  return printPDF(pdfUrl, PRINTERS.RECIBOS, "fit,monochrome");
};

// Imprimir TICKET de 58mm en impresora térmica (Xprinter XP-P502A por Bluetooth/USB).
// Detecta automáticamente la impresora térmica por su nombre. Si no la encuentra,
// devuelve la lista de impresoras disponibles para que el usuario indique el nombre.
export const printTicket58 = async (pdfUrl, printerNameOverride = null) => {
  const printers = await getPrinters();

  // Excluir impresoras duplicadas "(copy ...)" y sin driver (suelen fallar / estar sin conexión)
  const usable = printers.filter(p => !/copy/i.test(p) && !/no est.? disponible/i.test(p));

  let target = printerNameOverride;

  // 1. Coincidencia EXACTA con el nombre preferido (POS-58)
  if (!target) {
    target = usable.find(p => p.trim().toLowerCase() === PRINTERS.TICKET58_NAME.toLowerCase());
  }
  // 2. Coincidencia por patrón, ignorando copias
  if (!target) {
    target = usable.find(p => PRINTERS.TICKET58_PATTERNS.some(rx => rx.test(p)));
  }
  // 3. Último recurso: cualquier coincidencia (incluidas copias)
  if (!target) {
    target = printers.find(p => PRINTERS.TICKET58_PATTERNS.some(rx => rx.test(p)));
  }

  if (!target) {
    return {
      success: false,
      message: `No se encontró la impresora térmica de 58mm ("${PRINTERS.TICKET58_NAME}").\n` +
               `Impresoras disponibles: ${printers.join(", ") || "(ninguna)"}\n` +
               `Verifica que la POS-58 esté instalada y en línea.`
    };
  }

  // fit = ajusta el contenido al área imprimible; monochrome para térmica
  return printPDF(pdfUrl, target, "fit,monochrome");
};

// Diagnóstico - ejecutar desde consola: window.qzDiagnostico()
if (typeof window !== 'undefined') {
  window.qzDiagnostico = async () => {
    console.log('=== Diagnóstico Print Server ===');
    
    console.log('1. Verificando servidor...');
    const status = await checkPrintServer();
    if (!status.available) {
      console.error('   ERROR: Servidor no disponible en localhost:9120');
      console.log('   Ejecuta: iniciar_print_server.bat');
      return;
    }
    console.log('   Servidor OK');
    console.log(`   SumatraPDF: ${status.sumatra ? 'OK' : 'NO ENCONTRADO'}`);

    console.log('2. Buscando impresoras...');
    const printers = await getPrinters();
    console.log('   Impresoras:', printers);
    
    const etiquetas = printers.find(p => p.toLowerCase().includes('etiqueta'));
    const canon = printers.find(p => p.toLowerCase().includes('canon'));
    
    console.log(`   Etiquetas: ${etiquetas || 'NO ENCONTRADA'}`);
    console.log(`   Recibos (Canon): ${canon || 'NO ENCONTRADA'}`);
    
    console.log('=== Fin diagnóstico ===');
  };
}

export default {
  PRINTERS,
  checkPrintServer,
  getPrinters,
  printPDF,
  printEtiquetas,
  printRecibo,
  printTicket58
};
